import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { CommonService } from 'src/app/provide/common.service';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ApiEndPoint } from 'src/app/config/api-end-point';

@Component({
    selector: 'app-aboutus',
    templateUrl: './aboutus.component.html',
    styleUrls: ['./aboutus.component.scss'],
    standalone: false
})
export class AboutusComponent implements OnInit {

  pageName: any;
  description: any;
  type: any;
  result: any;
  aboutData: any;
  isLoading: boolean = true;
  documentOnly: boolean = false;
  sanitizedPdfUrl: SafeResourceUrl | null = null;

  constructor(
    private fb: UntypedFormBuilder, 
    public commonService: CommonService, 
    private router: Router, 
    private route: ActivatedRoute,
    private sanitizer: DomSanitizer
  ) { }

  ngOnInit(): void {
    window.scrollTo(0, 0);
    
    // Check if this is a document-only view
    this.route.data.subscribe(data => {
      this.documentOnly = data.documentOnly === true;
    });
    
    this.getPrivacy();
  }

  getPrivacy() {
    this.isLoading = true;
    this.commonService.showSpinner();
    
    // Use the API endpoint from the config file
    this.commonService.getApi(ApiEndPoint.aboutUs, 0).subscribe(
      (res: any) => {
        if (res['responseCode'] == 200) {
          this.aboutData = res['result'];
          this.setDocumentTypeFlags();
          console.log('About Data:', this.aboutData);
          console.log('Document URL:', this.aboutData?.documentUrl);
          console.log('Document Type:', this.aboutData?.documentType);
          console.log('Is Supported Document:', this.isSupportedDocument());
          console.log('Is Office Document:', this.isOfficeDocument());
          console.log('Description:', this.aboutData?.description);
          
          this.commonService.hideSpinner();
          this.isLoading = false;
        } else {
          this.commonService.hideSpinner();
          this.isLoading = false;
          this.commonService.errorToast(res.responseMessage);
        }
      }, 
      err => {
        this.commonService.hideSpinner();
        this.isLoading = false;
        this.commonService.warningToast(err['error']['responseMessage']);
      }
    );
  }

  setDocumentTypeFlags() {
    const url = this.aboutData?.documentUrl?.toLowerCase() || '';
    console.log('DEBUG: Document URL:', this.aboutData?.documentUrl);
    console.log('DEBUG: isSupportedDocument:', this.isSupportedDocument());
    console.log('DEBUG: isOfficeDocument:', this.isOfficeDocument());
    console.log('DEBUG: isImageDocument:', this.isImageDocument());
    console.log('DEBUG: isTxtDocument:', this.isTxtDocument());
    // FORCE PDF logic for all documents for testing
    let pdfUrl = this.aboutData.documentUrl;
    if (pdfUrl && pdfUrl.endsWith('.pdf') && !pdfUrl.includes('#')) {
      pdfUrl += '#view=FitH';
    }
    this.sanitizedPdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(pdfUrl);
  }

  // Helper method to check if a URL is from S3
  private isS3Url(url: string): boolean {
    if (!url) return false;
    
    return url.includes('amazonaws.com') || 
           url.includes('s3.') || 
           url.includes('s3-');
  }

  // Determine if the document is a supported type (PDF or Text)
  isSupportedDocument(): boolean {
    if (!this.aboutData || !this.aboutData.documentUrl) return false;
    
    const url = this.aboutData.documentUrl.toLowerCase();
    const documentType = this.aboutData.documentType ? this.aboutData.documentType.toLowerCase() : '';

    return url.endsWith('.pdf') || documentType.includes('pdf');
  }

  // Determine if the document is an Office document (Word, Excel, PowerPoint)
  isOfficeDocument(): boolean {
    if (!this.aboutData || !this.aboutData.documentUrl) return false;
    
    const url = this.aboutData.documentUrl.toLowerCase();
    const officeExtensions = ['.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx'];
    
    return officeExtensions.some(ext => url.endsWith(ext)) || 
           (this.aboutData.documentType && 
            (this.aboutData.documentType.toLowerCase().includes('word') || 
             this.aboutData.documentType.toLowerCase().includes('excel') || 
             this.aboutData.documentType.toLowerCase().includes('powerpoint') ||
             this.aboutData.documentType.toLowerCase().includes('office')));
  }

  // New getter to provide safe URL for Office documents
  get safeOfficeDocumentUrl(): SafeResourceUrl {
    if (this.aboutData && this.aboutData.documentUrl) {
      const encodedUrl = encodeURIComponent(this.aboutData.documentUrl);
      return this.sanitizer.bypassSecurityTrustResourceUrl(
        `https://docs.google.com/viewer?url=${encodedUrl}&embedded=true`
      );
    }
    return this.sanitizer.bypassSecurityTrustResourceUrl('');
  }

  // Close the document view and return to the regular view
  closeDocumentView(): void {
    if (window.opener) {
      window.close(); // Close the tab if opened in a new tab
    } else {
      this.router.navigate(['/aboutUs']); // Navigate back to regular view
    }
  }

  openPdfInNewTab() {
    if (this.aboutData?.documentUrl) {
      window.open(this.aboutData.documentUrl, '_blank');
    }
  }

  isImageDocument(): boolean {
    if (!this.aboutData || !this.aboutData.documentUrl) return false;
    const url = this.aboutData.documentUrl.toLowerCase();
    return ['.jpg', '.jpeg', '.png', '.gif'].some(ext => url.endsWith(ext));
  }

  isTxtDocument(): boolean {
    if (!this.aboutData || !this.aboutData.documentUrl) return false;
    const url = this.aboutData.documentUrl.toLowerCase();
    return url.endsWith('.txt');
  }

  isOtherDocument(): boolean {
    if (!this.aboutData || !this.aboutData.documentUrl) return false;
    const url = this.aboutData.documentUrl.toLowerCase();
    return !this.isSupportedDocument() && !this.isImageDocument() && !this.isOfficeDocument() && !this.isTxtDocument();
  }
}
